import React, { useEffect, useMemo, useState } from 'react';
import { CalendarSearch, X } from 'lucide-react';
import { formatDateKey, formatDateMedium, formatTimeString, parseLocalDateTime } from '../../utils/timeUtils';
import { formatProviderName, type PreferredProviderOption } from './PreferredProviderDropdown';

/** The day (and optionally the time) the operator was hoping to book. */
export interface AvailabilityHelpChoice {
  /** YYYY-MM-DD. */
  date: string;
  /** "Morning" | "Afternoon" | "Evening" | a 24h "HH:MM", or '' for any time. */
  time: string;
  /** Display form of `time`, e.g. "1:00 PM". Empty when any time works. */
  timeLabel: string;
  /** Whose calendar to audit, when the service has more than one assignee. */
  assignedUserId: string;
}

interface AvailabilityHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Who the booking is for, named in the intro so the ask feels concrete. */
  customerName?: string;
  /** The day the customer asked for — the sensible default selection. */
  defaultDate?: string;
  /** The earliest day the engine actually offered, for contrast. */
  earliestOfferedLabel?: string | null;
  /** Everyone this service can be assigned to. A picker shows when >1. */
  assignees?: PreferredProviderOption[];
  /** Whoever the engine put on the first slot, pre-selected. */
  defaultAssigneeId?: string;
  onSubmit: (choice: AvailabilityHelpChoice) => void;
}

const TIME_CHOICES: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any time' },
  { value: 'Morning', label: 'Morning' },
  { value: 'Afternoon', label: 'Afternoon' },
  { value: 'Evening', label: 'Evening' },
];

const EXACT = 'exact';

/**
 * The bridge between "this date should be available" and Routy's answer.
 *
 * Operators routinely believe a day is open when their connected calendar has
 * quietly closed it, so instead of explaining instant booking to them, this
 * collects the one thing only they know — the day (and time) they were
 * actually after — and hands it to Routy to audit.
 */
const AvailabilityHelpModal: React.FC<AvailabilityHelpModalProps> = ({
  isOpen,
  onClose,
  customerName,
  defaultDate,
  earliestOfferedLabel,
  assignees = [],
  defaultAssigneeId,
  onSubmit,
}) => {
  const today = useMemo(() => formatDateKey(new Date()), []);
  const [date, setDate] = useState(defaultDate || today);
  const [timeChoice, setTimeChoice] = useState('');
  const [exactTime, setExactTime] = useState('');
  const [assigneeId, setAssigneeId] = useState(defaultAssigneeId || '');

  // On a multi-assignee service the audit is only meaningful against ONE
  // person's calendar, so there is no "any of them" option to fall back on.
  const needsAssignee = assignees.length > 1;
  const resolvedAssigneeId = needsAssignee
    ? assigneeId
    : assignees[0]?.id || defaultAssigneeId || '';

  // Reopening should start from the customer's request again, not from
  // whatever was typed the last time the modal was used.
  useEffect(() => {
    if (!isOpen) return;
    setDate(defaultDate || today);
    setTimeChoice('');
    setExactTime('');
    setAssigneeId(defaultAssigneeId || '');
  }, [isOpen, defaultDate, today, defaultAssigneeId]);

  if (!isOpen) return null;

  const usingExact = timeChoice === EXACT;
  const canSubmit = !!date && (!usingExact || !!exactTime) && (!needsAssignee || !!assigneeId);
  const parsedDate = parseLocalDateTime(date);

  const submit = () => {
    if (!canSubmit) return;
    const time = usingExact ? exactTime : timeChoice;
    onSubmit({
      date,
      time,
      timeLabel: usingExact ? formatTimeString(exactTime) : timeChoice,
      assignedUserId: resolvedAssigneeId,
    });
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-surface shadow-2xl">
        <div className="border-b border-ink-100 bg-gradient-to-b from-blue-50/60 to-white px-5 pb-4 pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="flex-shrink-0 rounded-lg bg-blue-100 p-2 ring-1 ring-blue-200/60">
                <CalendarSearch className="h-4 w-4 text-blue-600" />
              </div>
              <h3 className="text-15 font-semibold leading-tight text-ink-900">Don't see the time you need?</h3>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 rounded-lg p-1.5 text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="space-y-4 px-5 py-5">
          <p className="text-sm leading-relaxed text-ink-700">
            Pick the day{customerName ? ` you wanted for ${customerName}` : ' you were hoping for'} and Routy
            will break down exactly what's holding it
            {earliestOfferedLabel ? ` — and why the earliest offer is ${earliestOfferedLabel}` : ''}.
          </p>

          {needsAssignee && (
            <div>
              <span className="mb-1.5 block text-xs font-semibold text-ink-700">Whose calendar?</span>
              <div className="flex flex-wrap gap-1.5">
                {assignees.map((user) => {
                  const active = assigneeId === user.id;
                  const name = formatProviderName(user);
                  return (
                    // eslint-disable-next-line no-restricted-syntax -- assignee chip with avatar; no dedicated primitive exists yet
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => setAssigneeId(user.id)}
                      className={`flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-3 text-xs font-medium transition-colors ${
                        active
                          ? 'border-blue-300 bg-blue-50 text-blue-700'
                          : 'border-ink-200 bg-surface text-ink-600 hover:border-ink-300 hover:bg-ink-50'
                      }`}
                    >
                      {user.photoUrl ? (
                        <img src={user.photoUrl} alt="" className="h-5 w-5 rounded-full object-cover" />
                      ) : (
                        <span
                          className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold text-white"
                          style={{ backgroundColor: user.color || '#64748b' }}
                        >
                          {name.trim().charAt(0).toUpperCase()}
                        </span>
                      )}
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label htmlFor="availability-help-date" className="mb-1.5 block text-xs font-semibold text-ink-700">
              Day you want
            </label>
            {/* Clicking anywhere on the field opens the picker — the native
                calendar icon is a small target and missing it just focuses the
                text, which reads as the control being broken. */}
            <input
              id="availability-help-date"
              type="date"
              value={date}
              min={today}
              onChange={(e) => setDate(e.target.value)}
              onClick={(e) => e.currentTarget.showPicker?.()}
              onFocus={(e) => e.currentTarget.showPicker?.()}
              className="w-full cursor-pointer rounded-xl border border-ink-200 bg-surface px-3 py-2.5 text-sm text-ink-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
            {parsedDate && <p className="mt-1 text-xs text-ink-500">{formatDateMedium(parsedDate)}</p>}
          </div>

          <div>
            <span className="mb-1.5 block text-xs font-semibold text-ink-700">
              Time of day <span className="font-normal text-ink-400">(optional)</span>
            </span>
            <div className="flex flex-wrap gap-1.5">
              {[...TIME_CHOICES, { value: EXACT, label: 'Exact time' }].map((choice) => {
                const active = timeChoice === choice.value;
                return (
                  // eslint-disable-next-line no-restricted-syntax -- segmented time-of-day chip; no dedicated primitive exists yet
                  <button
                    key={choice.value || 'any'}
                    type="button"
                    onClick={() => setTimeChoice(choice.value)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      active
                        ? 'border-blue-300 bg-blue-50 text-blue-700'
                        : 'border-ink-200 bg-surface text-ink-600 hover:border-ink-300 hover:bg-ink-50'
                    }`}
                  >
                    {choice.label}
                  </button>
                );
              })}
            </div>
            {usingExact && (
              <input
                type="time"
                value={exactTime}
                // Appointments are never set to the minute, and a free-typed
                // 10:37 just produces a question nobody meant to ask.
                step={300}
                onChange={(e) => setExactTime(e.target.value)}
                onClick={(e) => e.currentTarget.showPicker?.()}
                onFocus={(e) => e.currentTarget.showPicker?.()}
                className="mt-2 w-full cursor-pointer rounded-xl border border-ink-200 bg-surface px-3 py-2.5 text-sm text-ink-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-ink-100 bg-ink-50 px-5 py-3">
          {/* eslint-disable-next-line no-restricted-syntax -- matches the surrounding CreateBookingModal footer buttons */}
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-ink-600 transition-colors hover:text-ink-900"
          >
            Cancel
          </button>
          {/* eslint-disable-next-line no-restricted-syntax -- matches the surrounding CreateBookingModal footer buttons */}
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="rounded-lg bg-ink-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-ink-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Show me what's blocking it
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityHelpModal;
